import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api/client';
import { Section, SectionEyebrow, SectionTitle } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Divider } from '../components/ui/Primitives';

const RANGES = [7, 30, 90] as const;

export const Route = createFileRoute('/_app/stats')({
  component: StatsPage,
});

function StatsPage() {
  const [range, setRange] = useState<typeof RANGES[number]>(30);
  const { data, isLoading } = useQuery({
    queryKey: ['stats', range],
    queryFn: () => api.stats.get(range),
  });

  return (
    <>
      <Section surface="canvas">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div className="min-w-0">
            <SectionEyebrow>STATS</SectionEyebrow>
            <SectionTitle size="section-heading" className="mt-3">What your data is telling you.</SectionTitle>
          </div>
          <div className="flex gap-1 shrink-0">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`pill-outline text-caption ${range === r ? 'bg-near-black text-canvas-white border-near-black' : ''}`}
                type="button"
              >
                {r}D
              </button>
            ))}
          </div>
        </div>

        {isLoading || !data ? (
          <p className="text-muted-slate">Loading…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              label="Mood"
              primary={data.mood.average ? `${data.mood.average}/5 avg` : '—'}
              lines={[
                data.mood.best ? `Best: ${data.mood.best.rating} on ${data.mood.best.date}` : null,
                data.mood.worst ? `Worst: ${data.mood.worst.rating} on ${data.mood.worst.date}` : null,
                data.mood.streak > 0 ? `${data.mood.streak}-day streak` : null,
              ].filter(Boolean) as string[]}
            />
            <SummaryCard
              label="Tasks"
              primary={`${data.tasks.completed} done`}
              lines={[
                `${data.tasks.added} added`,
                data.tasks.overdue > 0 ? `${data.tasks.overdue} overdue` : '0 overdue',
                data.tasks.mostActiveArea ? `Most active: ${data.tasks.mostActiveArea}` : null,
              ].filter(Boolean) as string[]}
            />
            <SummaryCard
              label="Journal"
              primary={`${data.journal.total} entries`}
              lines={[`${data.journal.thisWeek} this week`]}
            />
            <SummaryCard
              label="Focus"
              primary={`${data.focus.totalMinutes} min`}
              lines={[
                `${data.focus.averageMinutesPerDay} min/day avg`,
                data.focus.mostFocusedDay ? `Most focused: ${data.focus.mostFocusedDay}` : null,
              ].filter(Boolean) as string[]}
            />
          </div>
        )}
      </Section>

      {data?.insight && (
        <Section surface="dark">
          <div className="max-w-3xl">
            <p className="mono-label text-canvas-white/60">MOOD × PRODUCTIVITY INSIGHT</p>
            <h2 className="font-display text-section-heading text-canvas-white mt-3 text-balance">
              {data.insight.text}
            </h2>
            {!data.insight.hasEnoughData && (
              <p className="mt-4 text-body text-canvas-white/70">
                Log more mood and complete more tasks to unlock a richer pattern.
              </p>
            )}
          </div>
        </Section>
      )}

      {data && Object.keys(data.mood.byArea).length > 0 && (
        <Section surface="canvas">
          <SectionEyebrow>MOOD BY AREA</SectionEyebrow>
          <SectionTitle size="card-heading" className="mt-2">When you're well, what are you doing?</SectionTitle>
          <ul className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(data.mood.byArea)
              .sort((a, b) => (b[1] as number) - (a[1] as number))
              .map(([area, rating]) => (
                <Card key={area} className="!p-4">
                  <p className="field-label">{area}</p>
                  <p className="mt-2 text-feature-heading font-display text-ink">{rating}/5</p>
                </Card>
              ))}
          </ul>
        </Section>
      )}
    </>
  );
}

function SummaryCard({ label, primary, lines }: { label: string; primary: string; lines: string[] }) {
  return (
    <Card>
      <p className="field-label">{label}</p>
      <p className="mt-3 text-card-heading font-display text-ink">{primary}</p>
      <Divider className="my-4" />
      <ul className="flex flex-col gap-1.5">
        {lines.map((l, i) => (
          <li key={i} className="text-caption text-muted-slate">{l}</li>
        ))}
      </ul>
    </Card>
  );
}
